import { EDITIONS, type Edition, MOVIE_SORTS, type MovieSort } from '@zyx/shared';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/page-header';
import { MovieGrid } from '@/components/movie/movie-grid';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTerms } from '@/hooks/use-movies';
import { editionLabel } from '@/lib/format';

const SORT_LABELS: Readonly<Record<MovieSort, string>> = {
  relevance: 'Best match',
  published: 'Newest',
  release: 'Release date',
  added: 'Recently indexed',
  views: 'Most viewed',
  watchlist: 'Watchlist order',
  list: 'List order',
};

const ANY = 'any';

/** Browsing and search results: the filters live in the URL, so a result is a link. */
export function BrowsePage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? undefined;
  const edition = EDITIONS.find((candidate) => candidate === params.get('edition'));
  const sort = MOVIE_SORTS.find((candidate) => candidate === params.get('sort')) ?? (q === undefined ? 'published' : 'relevance');
  const termId = Number(params.get('genre') ?? '') || undefined;
  // Genres the library holds, most movies first: the filter offers nothing it lacks.
  const genres = useTerms({ kind: 'genre', limit: 200 });
  const sorts = MOVIE_SORTS.filter((candidate) => candidate !== 'watchlist' && candidate !== 'list' && (q !== undefined || candidate !== 'relevance'));

  const set = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value === undefined) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setParams(next);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={q === undefined ? 'Browse' : `Results for “${q}”`}
        description={q === undefined ? undefined : 'Codes match first; then titles, cast and genres, typos forgiven.'}
        actions={
          <>
            <Select value={termId === undefined ? ANY : String(termId)} onValueChange={(value) => set('genre', value === ANY ? undefined : value)}>
              <SelectTrigger className="w-44" aria-label="Genre">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All genres</SelectItem>
                {termId === undefined || genres.data?.terms.some((genre) => genre.termId === termId) === true ? null : (
                  <SelectItem value={String(termId)}>{genres.data === undefined ? 'Loading…' : 'This genre'}</SelectItem>
                )}
                {(genres.data?.terms ?? []).map((genre) => (
                  <SelectItem key={genre.termId} value={String(genre.termId)}>
                    {genre.name} ({genre.movieCount.toLocaleString()})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={edition ?? ANY} onValueChange={(value) => set('edition', value === ANY ? undefined : value)}>
              <SelectTrigger className="w-40" aria-label="Edition">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any edition</SelectItem>
                {EDITIONS.map((candidate: Edition) => (
                  <SelectItem key={candidate} value={candidate}>
                    {editionLabel(candidate)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(value) => set('sort', value)}>
              <SelectTrigger className="w-44" aria-label="Sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sorts.map((candidate) => (
                  <SelectItem key={candidate} value={candidate}>
                    {SORT_LABELS[candidate]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />
      <MovieGrid
        request={{ q, edition, sort, termId }}
        empty={
          q === undefined
            ? { title: 'Nothing matches these filters' }
            : { title: `Nothing found for “${q}”`, description: 'Try fewer words, a code such as DSOD-052, or a name. Only indexed movies can be found.' }
        }
      />
    </div>
  );
}
